import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect } from "react";
import { BRAND_TITLE_SUFFIX } from "@/data/marketing-seo";

const DEFAULT_OG_IMAGE = "https://padpulse.io/og-image.jpg";
const BASE_URL = "https://padpulse.io";

interface SEOProps {
  title: string;
  description: string;
  /** Short page name shown in the browser tab as "<tabTitle> · PadPulse for PadSplit hosts"
   *  (build F9). When omitted, `title` is used as-is for the tab. */
  tabTitle?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  canonical?: string;
  path?: string;
  /** Optional schema.org structured data injected as a JSON-LD <script>.
   *  Accepts a single object or an array of objects (e.g. BreadcrumbList +
   *  SoftwareApplication). Pass undefined to emit no structured data. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Adds `<meta name="robots" content="noindex,nofollow">` while the page is mounted. */
  noindex?: boolean;
}

const JSONLD_SCRIPT_ID = "seo-jsonld";

function setMetaTag(name: string, content: string, attribute: string = "name") {
  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonicalLink(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export function useSEO({ title, description, tabTitle, ogTitle, ogDescription, ogType, ogImage, canonical, path, jsonLd, noindex }: SEOProps) {
  // useSEO is only used by public marketing pages, so every tab title ends
  // with the brand lockup (F9): "<tabTitle> · PadPulse for PadSplit hosts", or
  // the full `title` as-is when no tabTitle is given.
  usePageTitle(tabTitle ? `${tabTitle}${BRAND_TITLE_SUFFIX}` : title, true);

  // Serialize once so the effect dependency is stable for inline-built objects
  // (which would otherwise be a new reference on every render).
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const resolvedCanonical = canonical || (path ? `${BASE_URL}${path}` : `${BASE_URL}/`);
    const resolvedOgImage = ogImage || DEFAULT_OG_IMAGE;

    setMetaTag("description", description);
    setMetaTag("og:title", ogTitle || title, "property");
    setMetaTag("og:description", ogDescription || description, "property");
    setMetaTag("og:type", ogType || "website", "property");
    setMetaTag("og:url", resolvedCanonical, "property");
    setMetaTag("og:image", resolvedOgImage, "property");
    setMetaTag("og:site_name", "PadPulse", "property");
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", ogTitle || title);
    setMetaTag("twitter:description", ogDescription || description);
    setMetaTag("twitter:image", resolvedOgImage);
    setCanonicalLink(resolvedCanonical);

    let ldScript = document.getElementById(JSONLD_SCRIPT_ID) as HTMLScriptElement | null;
    if (jsonLdString) {
      if (!ldScript) {
        ldScript = document.createElement("script");
        ldScript.id = JSONLD_SCRIPT_ID;
        ldScript.type = "application/ld+json";
        document.head.appendChild(ldScript);
      }
      ldScript.textContent = jsonLdString;
    } else if (ldScript) {
      ldScript.remove();
    }

    return () => {
      // Remove on unmount/navigation so stale structured data never lingers
      // on a page that does not declare its own.
      document.getElementById(JSONLD_SCRIPT_ID)?.remove();
    };
  }, [title, description, tabTitle, ogTitle, ogDescription, ogType, ogImage, canonical, path, jsonLdString]);

  useNoindex(Boolean(noindex));
}

const ROBOTS_META_ID = "seo-robots";

/** Marks the current page `noindex,nofollow` until it unmounts. */
export function useNoindex(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    let meta = document.getElementById(ROBOTS_META_ID) as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.id = ROBOTS_META_ID;
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex,nofollow";
    return () => {
      document.getElementById(ROBOTS_META_ID)?.remove();
    };
  }, [enabled]);
}
